import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Calendar, User, LogOut, Menu, X, Users, Bell, Check, CheckCheck, FileText, CalendarDays, BookOpen, Package, Wallet, ListChecks, FolderOpen, TrendingUp, UserCheck, MessageSquare, KeyRound, Eye, EyeOff, UserMinus, Award, ScrollText, ShieldPlus, BadgeCheck, Clock, ScanLine, NotebookPen, AlertTriangle, Download, Coins, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import PWAInstallPrompt from './PWAInstallPrompt';
import { NotifyPrompt } from './NotifyPrompt';
import { UrgentAlertOverlay } from './UrgentAlertOverlay';
import { useNotificationChime } from './notifySound';
import CelebrationsOverlay from '../CelebrationsOverlay';

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

// Force cache clear via server version check
(async function checkVersion() {
  try {
    const res = await fetch(`${API}/api/employee-portal/app-version`, { cache: 'no-store' });
    const data = await res.json();
    const serverVer = data.version;
    const localVer = localStorage.getItem('vynce_app_version');
    if (localVer !== serverVer) {
      localStorage.setItem('vynce_app_version', serverVer);
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) { await reg.unregister(); }
      }
      // Delete all caches
      if ('caches' in window) {
        const keys = await caches.keys();
        for (const k of keys) { await caches.delete(k); }
      }
      if (localVer) {
        window.location.reload(true);
        return;
      }
    }
  } catch (e) { /* offline or first load */ }
})();

/* ── Push helpers ──────────────────────────────────────────── */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function subscribeToPush(token, ask = false) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !VAPID_PUBLIC_KEY) return;
  try {
    const reg = await navigator.serviceWorker.register('/portal-sw.js');
    // iPhone only allows the permission request from a tap, so ask via the prompt button
    if (Notification.permission !== 'granted' && !ask) return;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
    await axios.post(`${API}/api/employee-portal/push-subscribe`, {
      subscription: sub.toJSON()
    }, { headers: { Authorization: `Bearer ${token}` } });
  } catch (e) { /* silent */ }
}

/* ── Notification type config ──────────────────────────────── */
const NOTIF_ICONS = {
  leave_approved: { color: 'bg-emerald-100 text-emerald-600', icon: '✓' },
  leave_rejected: { color: 'bg-red-100 text-red-600', icon: '✕' },
  leave_applied: { color: 'bg-amber-100 text-amber-600', icon: '↗' },
  welcome: { color: 'bg-sky-100 text-sky-600', icon: '★' },
  password_changed: { color: 'bg-indigo-100 text-indigo-600', icon: '🔑' },
  password_reset: { color: 'bg-orange-100 text-orange-600', icon: '↺' },
  doc_expiry: { color: 'bg-rose-100 text-rose-600', icon: '⚠' },
};

const timeAgo = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const PortalLayout = ({ children }) => {
  const [sideOpen, setSideOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [logbookEnabled, setLogbookEnabled] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwData, setPwData] = useState({ current: '', new_pw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showIosInstall, setShowIosInstall] = useState(false);
  const isStandalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const emp = JSON.parse(localStorage.getItem('portal_employee') || '{}');
  const token = localStorage.getItem('portal_token');

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/employee-portal/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    subscribeToPush(token);
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token, fetchNotifications]);
  useNotificationChime(unreadCount, fetchNotifications);

  // Check whether HR has enabled the Logbook for this employee (controls nav item)
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/employee-portal/logbook/access`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLogbookEnabled(!!r.data?.enabled))
      .catch(() => setLogbookEnabled(false));
  }, [token]);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_employee');
    navigate('/employee-portal/login');
  };

  const handleInstallApp = async () => {
    setSideOpen(false);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const promptEvent = window.__deferredInstallPrompt;
    if (promptEvent) {
      promptEvent.prompt();
      try { await promptEvent.userChoice; } catch { /* ignore */ }
      window.__deferredInstallPrompt = null;
      return;
    }
    if (isIOS) {
      setShowIosInstall(true);
      return;
    }
    alert('The app is either already installed or your browser handles install from its menu (look for an install/"+" icon in the address bar). Open the portal directly (not inside another app) to install.');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (pwData.new_pw.length < 4) { setPwError('Password must be at least 4 characters'); return; }
    if (pwData.new_pw !== pwData.confirm) { setPwError('New passwords do not match'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/api/employee-portal/change-password`, {
        current_password: pwData.current,
        new_password: pwData.new_pw,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowChangePw(false);
      setPwData({ current: '', new_pw: '', confirm: '' });
      alert('Password changed successfully. Please sign in again.');
      handleLogout();
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/api/employee-portal/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/api/employee-portal/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  // Map a notification to its target portal route (deep link)
  const notifTarget = (n) => {
    const t = n?.type || '';
    const data = n?.data || {};
    // Salary advance — manager-side notifications go to manager tab
    if (t === 'salary_advance_applied' || t === 'salary_advance_pending_manager') {
      const rid = data.request_id ? `&highlight=${encodeURIComponent(data.request_id)}` : '';
      return `/employee-portal/salary-advance?tab=manager${rid}`;
    }
    if (t.startsWith('salary_advance_')) {
      const rid = data.request_id ? `?highlight=${encodeURIComponent(data.request_id)}` : '';
      return `/employee-portal/salary-advance${rid}`;
    }
    // Leaves
    if (t === 'leave_pending' || t === 'leave_applied') return '/employee-portal/leaves?tab=approvals';
    if (t.startsWith('leave_')) return '/employee-portal/leaves';
    // Appraisal — deep-link to the specific cycle so the page can auto-expand it
    if (t === 'appraisal') {
      const cid = data.cycle_id ? `?highlight=${encodeURIComponent(data.cycle_id)}` : '';
      return `/employee-portal/appraisal${cid}`;
    }
    // Letters
    if (t === 'experience_certificate' || t === 'salary_certificate') return '/employee-portal/my-letters';
    // Tasks / To-Do
    if (t === 'task_assigned') return '/employee-portal/todos';
    if (t === 'task_approved' || t === 'task_rejected') return '/employee-portal/todos';
    // Grievance / HR messages
    if (t === 'grievance_reply') return '/employee-portal/message-hr';
    // Memos — memo_reminder includes memo_id; use it to highlight the card
    if (t === 'memo' || t === 'memo_assigned' || t === 'memo_reminder') {
      const mid = data.memo_id ? `?highlight=${encodeURIComponent(data.memo_id)}` : '';
      return `/employee-portal/memos${mid}`;
    }
    // Documents
    if (t === 'document' || t === 'document_uploaded') return '/employee-portal/documents';
    // Document expiry — deep-link to Documents and blink the section in question.
    // The `highlight` slug is set by the backend (passport / visa / labour-card).
    if (t === 'doc_expiry') {
      const slug = data.highlight || (data.doc_type || '').toLowerCase().replace(/\s+/g, '-');
      return slug
        ? `/employee-portal/documents?highlight=${encodeURIComponent(slug)}`
        : '/employee-portal/documents';
    }
    // Insurance
    if (t === 'insurance' || t === 'insurance_assigned') return '/employee-portal/insurance';
    // Account / profile
    if (t === 'welcome' || t === 'password_changed' || t === 'password_reset') return '/employee-portal/profile';
    // Custom URL stored in notification data
    if (data && typeof data.url === 'string' && data.url) return data.url;
    return null;
  };

  const handleNotifClick = (n) => {
    if (!n.read) markRead(n.id);
    setBellOpen(false);
    const url = notifTarget(n);
    if (url) navigate(url);
  };

  const isManager = ['Manager', 'Management', 'Top Management', 'Owner'].includes(emp.employee_role);

  const isHRDept = (emp.department || '').toUpperCase() === 'HUMAN RESOURCE';

  const navItems = [
    { to: '/employee-portal', icon: Home, label: 'Home', end: true },
    { to: '/employee-portal/leaves', icon: Calendar, label: 'Leaves' },
    { to: '/employee-portal/leave-forecast', icon: TrendingUp, label: 'Leave Forecast' },
    { to: '/employee-portal/documents', icon: FolderOpen, label: 'Documents' },
    { to: '/employee-portal/assets', icon: Package, label: 'Assigned Assets' },
    { to: '/employee-portal/payroll', icon: Wallet, label: 'Payroll' },
    { to: '/employee-portal/payslips', icon: FileText, label: 'Payslips' },
    { to: '/employee-portal/leave-salary', icon: CalendarDays, label: 'Leave Salary' },
    { to: '/employee-portal/todos', icon: ListChecks, label: 'To-Do' },
    { to: '/employee-portal/message-hr', icon: MessageSquare, label: isHRDept ? 'Messages' : 'Message HR' },
    { to: '/employee-portal/offboarding', icon: UserMinus, label: 'Off-boarding' },
    { to: '/employee-portal/appraisal', icon: Award, label: 'Appraisal' },
    { to: '/employee-portal/memos', icon: ScrollText, label: 'Company Memos' },
    { to: '/employee-portal/insurance', icon: ShieldPlus, label: 'My Insurance' },
    { to: '/employee-portal/my-letters', icon: BadgeCheck, label: 'My Letters' },
    { to: '/employee-portal/warning-letters', icon: AlertTriangle, label: 'Warning Letters' },
    { to: '/employee-portal/salary-advance', icon: Wallet, label: 'Salary Advance' },
    { to: '/employee-portal/eosb', icon: Coins, label: 'EOSB / Gratuity' },
    { to: '/employee-portal/clock-in', icon: ScanLine, label: 'Clock In/Out' },
    { to: '/employee-portal/clock-pin', icon: KeyRound, label: 'Clock-In PIN' },
    { to: '/employee-portal/clock-records', icon: Clock, label: 'Clock Records' },
    ...(logbookEnabled ? [{ to: '/employee-portal/logbook', icon: NotebookPen, label: 'Logbook' }] : []),
    ...(isManager ? [{ to: '/employee-portal/team-todos', icon: UserCheck, label: 'Team Tasks' }] : []),
    ...(isManager ? [{ to: '/employee-portal/manager-warnings', icon: ShieldAlert, label: 'Issue Warnings' }] : []),
    { to: '/employee-portal/my-team', icon: Users, label: 'My Team' },
    ...((isManager || isHRDept || emp.employee_code === 'EMP0001') ? [{ to: '/employee-portal/leave-calendar', icon: CalendarDays, label: 'Leave Calendar' }] : []),
    { to: '/employee-portal/handbook', icon: BookOpen, label: 'Handbook' },
    { to: '/employee-portal/profile', icon: User, label: 'Profile' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50 flex flex-col" data-testid="portal-layout">
      {/* Top bar */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <button onClick={() => setSideOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-100 transition" data-testid="portal-menu-btn">
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          {emp.photo_url ? (
            <img src={`${API}${emp.photo_url}`} alt="" className="w-8 h-8 rounded-full object-cover border" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
              <User className="h-4 w-4 text-sky-600" />
            </div>
          )}
          <div>
            <p className="text-sm font-semibold leading-tight">{emp.name || 'Employee'}</p>
            <p className="text-[10px] text-gray-400">{emp.department} | {emp.employee_code}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Bell Icon */}
          <div className="relative" ref={bellRef}>
            <button
              onClick={() => setBellOpen(!bellOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition relative"
              data-testid="portal-bell-btn"
            >
              <Bell className="h-5 w-5 text-gray-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {bellOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border z-[80] max-h-[70vh] flex flex-col" data-testid="notification-dropdown">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h4 className="text-sm font-bold text-gray-900">Notifications</h4>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-[10px] text-sky-600 font-medium flex items-center gap-1 hover:underline" data-testid="mark-all-read-btn">
                      <CheckCheck className="h-3 w-3" /> Mark all read
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-gray-200" />
                      <p className="text-xs text-gray-400">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map(n => {
                      const cfg = NOTIF_ICONS[n.type] || { color: 'bg-gray-100 text-gray-500', icon: '•' };
                      return (
                        <div
                          key={n.id}
                          className={`px-4 py-3 border-b last:border-0 flex items-start gap-3 transition cursor-pointer hover:bg-gray-50 ${!n.read ? 'bg-sky-50/40' : ''}`}
                          onClick={() => handleNotifClick(n)}
                          data-testid={`notification-${n.id}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${cfg.color}`}>
                            {cfg.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-xs leading-tight ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                              {!n.read && <span className="w-2 h-2 rounded-full bg-sky-500 flex-shrink-0 mt-1" />}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                            <p className="text-[10px] text-gray-300 mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <button onClick={handleLogout} className="p-2 text-gray-400 hover:text-red-500 transition" data-testid="portal-logout-btn">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Side Panel Overlay */}
      {sideOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] transition-opacity" onClick={() => setSideOpen(false)} data-testid="portal-overlay" />
      )}

      {/* Side Panel */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 bg-white shadow-2xl z-[70] transform transition-transform duration-300 ease-in-out flex flex-col ${sideOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="portal-side-panel"
      >
        <div className="p-5 border-b bg-gradient-to-br from-sky-500 to-blue-600">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-bold text-sm tracking-wide">EMPLOYEE PORTAL</span>
            <button onClick={() => setSideOpen(false)} className="p-1 rounded-full hover:bg-white/20 transition" data-testid="portal-close-panel-btn">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            {emp.photo_url ? (
              <img src={`${API}${emp.photo_url}`} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white/50" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{emp.name || 'Employee'}</p>
              <p className="text-[11px] text-sky-100">{emp.position || emp.department}</p>
              <p className="text-[10px] text-sky-200">{emp.employee_code}</p>
            </div>
          </div>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto pb-44">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setSideOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive ? 'bg-sky-50 text-sky-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
              data-testid={`portal-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t space-y-1">
          {!isStandalone && (
            <button
              onClick={handleInstallApp}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-sky-600 hover:bg-sky-50 transition w-full"
              data-testid="portal-install-app-btn"
            >
              <Download className="h-5 w-5" />
              Install App
            </button>
          )}
          <button
            onClick={() => { setSideOpen(false); setShowChangePw(true); }}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition w-full"
            data-testid="portal-change-password-btn"
          >
            <KeyRound className="h-5 w-5" />
            Change Password
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition w-full"
            data-testid="portal-side-logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Change Password Dialog */}
      {showChangePw && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="change-password-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowChangePw(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-sm w-full z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-sky-600" /> Change Password
              </h3>
              <button onClick={() => setShowChangePw(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={pwData.current}
                    onChange={e => setPwData(p => ({ ...p, current: e.target.value }))}
                    required
                    className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 pr-10"
                    placeholder="Enter current password"
                    data-testid="pw-current"
                  />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-2.5 top-2.5 text-gray-400">
                    {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={pwData.new_pw}
                    onChange={e => setPwData(p => ({ ...p, new_pw: e.target.value }))}
                    required
                    className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 pr-10"
                    placeholder="Enter new password"
                    data-testid="pw-new"
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2.5 top-2.5 text-gray-400">
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={pwData.confirm}
                  onChange={e => setPwData(p => ({ ...p, confirm: e.target.value }))}
                  required
                  className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  placeholder="Re-enter new password"
                  data-testid="pw-confirm"
                />
              </div>
              {pwError && <p className="text-xs text-red-500 text-center">{pwError}</p>}
              <button
                type="submit"
                disabled={pwLoading}
                className="w-full py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                data-testid="pw-submit"
              >
                {pwLoading ? 'Changing...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* iOS Install Instructions Dialog */}
      {showIosInstall && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" data-testid="ios-install-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowIosInstall(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 mx-4 max-w-sm w-full z-10 text-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mx-auto mb-3">
              <Download className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Install on iPhone / iPad</h3>
            <p className="text-sm text-gray-500 mb-4">
              In Safari, tap the <strong>Share</strong> button, then choose <strong>"Add to Home Screen"</strong> to install the Vynce Portal app.
            </p>
            <button
              onClick={() => setShowIosInstall(false)}
              className="w-full py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium transition"
              data-testid="ios-install-close-btn"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <UrgentAlertOverlay token={token} />
      <NotifyPrompt onEnable={() => subscribeToPush(token, true)} />
      <main className="flex-1 overflow-y-auto px-4 py-4">
        {children}
      </main>

      {/* PWA Install Banner */}
      <PWAInstallPrompt />

      {/* Global birthday + anniversary confetti */}
      <CelebrationsOverlay />
    </div>
  );
};

export default PortalLayout;
